import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  FileInput,
  Group,
  List,
  Loader,
  Pagination,
  Progress,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconFileImport, IconSearch, IconUpload } from '@tabler/icons-react';
import {
  simpleClassifierIds,
  type ReferenceBookMetaDto,
  type ReferenceCodeDto,
  type SimpleClassifierId,
  type SimpleClassifierRow,
} from '@expeditor/shared';
import { referenceDataApi } from '../../shared/api/reference-data';
import { colIndex, parseGrid } from '../../shared/lib/csv';

const CHUNK = 500;

const TITLES: Record<SimpleClassifierId, { title: string; badge: string; hasEn: boolean }> = {
  country: { title: 'Классификатор стран (ФТС)', badge: 'страны', hasEn: false },
  packaging: { title: 'Классификатор видов упаковки (ФТС)', badge: 'упаковка', hasEn: true },
  'delivery-terms': { title: 'Условия поставки (Incoterms)', badge: 'условия поставки', hasEn: false },
  transport: { title: 'Классификатор видов транспорта (ФТС)', badge: 'виды транспорта', hasEn: false },
  units: { title: 'Классификатор единиц измерения (ФТС)', badge: 'единицы измерения', hasEn: false },
  currency: { title: 'Классификатор валют (ФТС)', badge: 'валюты', hasEn: false },
};

function parseRows(text: string): SimpleClassifierRow[] {
  const { rows, header } = parseGrid(text);
  const iCode = colIndex(header, ['kod', 'bukvenny_kod', 'code', 'код'], 0);
  const iName = colIndex(
    header,
    ['kratkoe_nazvanie', 'naimenovanie_rus', 'polnoe_naimenovanie', 'naimenovanie', 'name', 'наименование'],
    1,
  );
  const iEn = colIndex(header, ['naimenovanie_angl', 'name_en', 'nameen'], -1);
  const iNote = colIndex(
    header,
    [
      'primechanie',
      'uslovnoe_oboznachenie',
      'znachenie_geograficheskogo_punkta',
      'note',
      'примечание',
    ],
    -1,
  );
  const out: SimpleClassifierRow[] = [];
  for (const c of rows) {
    const code = (c[iCode] ?? '').trim();
    const name = (c[iName] ?? '').trim();
    if (!code || !name) continue;
    out.push({
      code,
      name: name.slice(0, 500),
      nameEn: iEn >= 0 ? (c[iEn] ?? '').trim().slice(0, 500) || null : null,
      note: iNote >= 0 ? (c[iNote] ?? '').trim().slice(0, 1000) || null : null,
    });
  }
  return out;
}

function dedupe(rows: SimpleClassifierRow[]): SimpleClassifierRow[] {
  const map = new Map<string, SimpleClassifierRow>();
  for (const r of rows) map.set(r.code, r);
  return [...map.values()];
}

/** Админ: просмотр/поиск и импорт простого классификатора (страны/упаковка/условия). */
/** Строк на страницу в админ-просмотре классификатора. */
const PAGE_SIZE = 50;

export function ClassifierPage() {
  const params = useParams();
  const id = (
    (simpleClassifierIds as readonly string[]).includes(params.id ?? '') ? params.id : 'country'
  ) as SimpleClassifierId;
  const cfg = TITLES[id];

  const [meta, setMeta] = useState<ReferenceBookMetaDto | null>(null);
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [parsed, setParsed] = useState<SimpleClassifierRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReferenceCodeDto[]>([]);
  const [searching, setSearching] = useState(false);
  // Постраничный просмотр: классификаторы бывают большими (валюты — 170,
  // упаковка — 365 строк).
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setMeta(null);
    setText('');
    setParsed([]);
    setQuery('');
    setResults([]);
    setPage(1);
    setTotal(0);
    setOkMsg(null);
    setError(null);
    referenceDataApi
      .classifierMeta(id)
      .then(setMeta)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить сведения'));
  }, [id]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    setSearching(true);
    const t = setTimeout(() => {
      referenceDataApi
        .classifierList(id, query, page, PAGE_SIZE)
        .then((r) => {
          setResults(r.rows);
          setTotal(r.total);
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
        })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [id, query, page]);

  const onText = (value: string) => {
    setText(value);
    setParsed(dedupe(parseRows(value)));
    setOkMsg(null);
  };
  const onFile = async (file: File | null) => {
    if (!file) return;
    onText(await file.text());
  };

  const doImport = async () => {
    if (parsed.length === 0) return;
    if (!window.confirm(`Загрузить ${parsed.length} строк? Текущий классификатор будет заменён.`))
      return;
    setBusy(true);
    setError(null);
    setOkMsg(null);
    setProgress({ done: 0, total: parsed.length });
    try {
      let last: ReferenceBookMetaDto | null = null;
      for (let i = 0; i < parsed.length; i += CHUNK) {
        last = await referenceDataApi.classifierImport(id, {
          rows: parsed.slice(i, i + CHUNK),
          replace: i === 0,
          source: i === 0 ? source.trim() || undefined : undefined,
        });
        setProgress({ done: Math.min(i + CHUNK, parsed.length), total: parsed.length });
      }
      if (last) setMeta(last);
      setOkMsg(`Загружено строк: ${last?.rowCount ?? parsed.length}.`);
      setText('');
      setParsed([]);
      setQuery('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка импорта');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const preview = parsed.slice(0, 5);

  return (
    <Stack>
      <Anchor component={Link} to="/manager/reference-data" size="sm">
        <Group gap={4}>
          <IconArrowLeft size={14} /> Все справочники
        </Group>
      </Anchor>
      <Title order={2}>{cfg.title}</Title>

      {error && <Alert color="red">{error}</Alert>}
      {okMsg && <Alert color="teal">{okMsg}</Alert>}

      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text fw={500}>Текущее состояние</Text>
            <Text size="sm" c="dimmed">
              Источник: {meta?.source ?? '—'}
            </Text>
            <Text size="sm" c="dimmed">
              Обновлён: {meta?.updatedAt ? new Date(meta.updatedAt).toLocaleString('ru-RU') : '—'}
            </Text>
          </Stack>
          <Badge size="lg" variant="light">
            {meta?.rowCount ?? 0} строк
          </Badge>
        </Group>
      </Card>

      <Divider label="Просмотр" labelPosition="left" />
      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Поиск по коду или наименованию"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        rightSection={searching ? <Loader size="xs" /> : null}
      />
      {results.length > 0 ? (
        <>
          <Text size="sm" c="dimmed">
            {query.trim() ? `Найдено: ${total}` : `Всего строк: ${total}`}
            {total > PAGE_SIZE ? ` · страница ${page} из ${Math.ceil(total / PAGE_SIZE)}` : ''}
          </Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={100}>Код</Table.Th>
                <Table.Th>Наименование</Table.Th>
                <Table.Th w={220}>Доп.</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.map((r) => (
                <Table.Tr key={r.code}>
                  <Table.Td>{r.code}</Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>{r.detail ?? ''}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {total > PAGE_SIZE && (
            <Group justify="center">
              <Pagination
                value={page}
                onChange={setPage}
                total={Math.ceil(total / PAGE_SIZE)}
                siblings={1}
                withEdges
              />
            </Group>
          )}
        </>
      ) : (
        !searching && (
          <Text size="sm" c="dimmed">
            {query.trim() ? 'Ничего не найдено.' : 'Классификатор пуст — загрузите данные ниже.'}
          </Text>
        )
      )}

      <Divider label="Импорт" labelPosition="left" />
      <Alert color="blue" variant="light">
        <Text size="sm">
          Файл CSV/TSV. Колонки распознаются по заголовку (<Code>kod, naimenovanie…</Code>
          {cfg.hasEn ? ', naimenovanie_angl' : ''}, <Code>primechanie</Code>) или по порядку.
          Разделитель и заголовок определяются автоматически. Загрузка полностью заменяет
          классификатор.
        </Text>
        <List size="sm" mt={4}>
          <List.Item>Обязательны код и наименование.</List.Item>
        </List>
      </Alert>

      <FileInput
        label="Файл классификатора (.csv / .tsv)"
        placeholder="Выберите файл"
        accept=".csv,.tsv,.txt"
        leftSection={<IconFileImport size={16} />}
        value={null}
        onChange={onFile}
        disabled={busy}
      />
      <Textarea
        label="…или вставьте содержимое"
        autosize
        minRows={3}
        maxRows={8}
        value={text}
        onChange={(e) => onText(e.currentTarget.value)}
        disabled={busy}
      />
      <TextInput
        label="Источник / версия (метка)"
        value={source}
        onChange={(e) => setSource(e.currentTarget.value)}
        disabled={busy}
      />

      {parsed.length > 0 && (
        <Card withBorder padding="sm">
          <Text size="sm" mb={6}>
            Распознано строк: <b>{parsed.length}</b>. Предпросмотр первых {preview.length}:
          </Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Код</Table.Th>
                <Table.Th>Наименование</Table.Th>
                {cfg.hasEn && <Table.Th>Англ.</Table.Th>}
                <Table.Th>Примечание</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {preview.map((r) => (
                <Table.Tr key={r.code}>
                  <Table.Td>{r.code}</Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  {cfg.hasEn && <Table.Td>{r.nameEn}</Table.Td>}
                  <Table.Td>{r.note}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {progress && (
        <Stack gap={4}>
          <Progress value={(progress.done / progress.total) * 100} animated />
          <Text size="xs" c="dimmed">
            Загружено {progress.done} из {progress.total}…
          </Text>
        </Stack>
      )}

      <Group>
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={doImport}
          loading={busy}
          disabled={parsed.length === 0}
        >
          Импортировать ({parsed.length})
        </Button>
      </Group>
    </Stack>
  );
}
